import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { JbCart, JbCartItem } from '@jbhi-fi/apis/src/shopify/types/JbCart';
import { ShopifyStorefrontClient } from '../../../api/shopify/ShopifyStorefrontClient';
import { trackRemoveFromCartShopify } from '../trackRemoveFromCartShopify';
import { trackViewCartShopify } from '../trackViewCartShopify';
import { hasReachedMaxQty } from '../../checkMaxQty/hasReachedMaxQty';
import { showMaxQtyAlert } from '../../checkMaxQty/showMaxQtyAlert';
import { getMarketplacePropertiesFromExistingItem } from '../refreshTieredProductDetails';
import { filterAndMapAttributes } from '@jbhi-fi/apis/src/utils/filterAndMapAttributes';
import { trackAddToCart } from '@jbhi-fi/utils/src/ga4Tracking';
import { getStoreCurrency } from '@jbhi-fi/utils/src/shopify/getStoreCurrency';
import { transformCartItemForMessages, transformCartItemForTracking } from '../transformCartItemForTracking';
import { delayWithCancel } from '@jbhi-fi/utils/src/delay';
import { getProductServiceVariantId, IsAttachedService } from '../../../templates/components/pdp/utils/product-service';
import { addServiceOffering } from '../addServiceOffering';
import { tryParseJson } from '@jbhi-fi/utils/src/JSON/tryParseJson';
import { ServiceOffering } from '@jbhi-fi/apis/src/services/types';
import { removeAttachedServiceOffering } from '../removeAttachedServiceOffering';
import { dataLayerPushServiceEvent } from '../../../templates/components/pdp/utils/utils';
import { ProductGtmData } from '@jbhi-fi/apis/src/algolia/types/ProductGtmData';
import {
  getCartLoading,
  getCartState,
  setCartLoading,
  updateCart,
  CART_UPDATE_EVENT,
  validateCart,
  updateCartState,
} from '../cartUtils';
import { getServiceOfferings, setServiceOfferings } from '../../parseServiceOfferings';
import { addServiceOfferings } from '../../../templates/components/pdp/installProductService/utils';

const storefrontClient = new ShopifyStorefrontClient();

export interface CartContextInterface {
  cart: JbCart | undefined;
  location: 'cart' | 'checkout' | 'minicart';
  removeItem: (item: JbCartItem, shouldShowWishlistCard?: boolean) => Promise<void>;
  clearWishlistCards: () => void;
  updateQuantity: (item: JbCartItem, quantity: number) => Promise<void>;
  removeAttachedService: (item: JbCartItem, message?: string) => Promise<void>;
  removeAttachedServices: (primaryItem: JbCartItem, services: Array<JbCartItem>) => Promise<void>;
  addBundleOffering: (item: JbCartItem) => Promise<void>;
  isMiniCartOpen: boolean;
  toggleMiniCart: (isOpen: boolean) => void;
  addServiceByOfferingId(
    cartItem: JbCartItem,
    serviceId: string,
    offeringId: string,
    caller?: string,
    enrichCallback?: (serviceOffering: ServiceOffering) => ServiceOffering
  ): Promise<void>;
  addServiceByOfferingId(
    cartItem: JbCartItem,
    serviceId: string,
    offeringIds: Array<string>,
    caller?: string,
    enrichCallback?: (serviceOffering: ServiceOffering) => ServiceOffering
  ): Promise<void>;
  addServiceByOfferingId(
    productSku: string,
    serviceId: string,
    offeringId: string,
    caller?: string,
    enrichCallback?: (serviceOffering: ServiceOffering) => ServiceOffering
  ): Promise<void>;
  removeServiceByOfferingId: (cartItem: JbCartItem, serviceId: string, offeringId: string) => Promise<void>;
}

export const CartContext = createContext<CartContextInterface | undefined>(undefined);

const getLocation = () => {
  if (window.location.pathname === '/cart') return 'cart';
  if (window.Shopify?.Checkout) return 'checkout';
  return 'minicart';
};

export const CartContextProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const [cart, setCart] = useState<JbCart | undefined>(getCartState);
  const [isMiniCartOpen, setIsMiniCartOpen] = useState<boolean>(false);
  const location = getLocation();
  const wishlistTimeouts = useRef<Array<() => void>>([]);
  const wishlistRemoveTimeout = window.Jbhifi.cartWishlistTimeout * 1000;

  const clearWishlistCards = () => {
    wishlistTimeouts.current.forEach(cancel => cancel());
    wishlistTimeouts.current = [];
  };

  const toggleMiniCart = (isOpen: boolean) => {
    if (isOpen) {
      trackViewCartShopify(cart, 'minicart');
    }
    setIsMiniCartOpen(isOpen);
  };

  useEffect(() => {
    const handleCartUpdate = (event: CustomEventInit<JbCart | undefined>) => {
      event.detail && setCart(event.detail);
    };
    window.addEventListener(CART_UPDATE_EVENT, handleCartUpdate);
    return () => {
      window.removeEventListener(CART_UPDATE_EVENT, handleCartUpdate);
    };
  }, []);

  const removeItem = async (item: JbCartItem, shouldShowWishlistCard?: boolean) => {
    if (getCartLoading()) return;

    clearWishlistCards();
    setCartLoading(true);
    const updatedCart = await storefrontClient.removeFromCart(item.line_id);
    const { validatedCart, cartApiResponse } = await validateCart(updatedCart);
    const newCart = validatedCart || updatedCart;
    setCartLoading(false);

    if (shouldShowWishlistCard) {
      setCart(prevCart => {
        if (!prevCart) return prevCart;
        return {
          ...prevCart,
          items: prevCart.items.map(cartItem =>
            cartItem.line_id === item.line_id ? { ...cartItem, removed: true } : cartItem
          ),
        };
      });
      const delay = delayWithCancel(wishlistRemoveTimeout);
      wishlistTimeouts.current.push(delay.cancel);
      await delay.promise;
    }
    setServiceOfferings(cartApiResponse, newCart);
    updateCartState(newCart);

    trackRemoveFromCartShopify(item);
    trackViewCartShopify(updatedCart, location);
  };

  const updateQuantity = async (item: JbCartItem, quantity: number) => {
    if (getCartLoading()) return;
    clearWishlistCards();
    const prevQuantity = item.quantity;
    const quantityChange = quantity - prevQuantity;
    if (hasReachedMaxQty(item?.id, quantityChange)) {
      showMaxQtyAlert();
      return;
    }
    setCartLoading(true);
    let updatedCart;
    if (prevQuantity === 1) {
      // We don't copy service related attributes when increasing quantity
      const { _serviceLink, _isAttachedService, _legacy_addons_line_key, ...properties } = item.properties;
      const marketplaceProperties = getMarketplacePropertiesFromExistingItem(item);
      updatedCart = await storefrontClient.addMultipleToCart(
        Array.from({ length: quantityChange }, () => ({
          variantId: item.id,
          attributes: filterAndMapAttributes({ ...properties, ...marketplaceProperties }),
        }))
      );
    } else {
      updatedCart = await storefrontClient.updateCartLine(item.line_id, quantity);
    }
    const { validatedCart } = await updateCart(updatedCart);
    if (quantityChange > 0) {
      trackAddToCart({
        location: location,
        currency: getStoreCurrency(),
        addedItems: transformCartItemForTracking({
          ...item,
          quantity: quantityChange,
        }),
        message: transformCartItemForMessages(item),
      });
    } else if (quantityChange < 0) {
      trackRemoveFromCartShopify(item, Math.abs(quantityChange));
    }
    trackViewCartShopify(validatedCart, location);
  };

  const addServiceByOfferingId = async (
    existingCartItemOrProductSku: JbCartItem | string,
    serviceId: string,
    offeringIdOrIds: string | Array<string>,
    caller?: string,
    enrichServiceOfferingCallback?: (serviceOffering: ServiceOffering) => ServiceOffering
  ) => {
    if (getCartLoading()) return;
    //get details of selected service offering from cart API
    const cartServiceOfferings = getServiceOfferings();
    if (!cartServiceOfferings) return;

    let existingCartItem: JbCartItem | undefined;

    if (typeof existingCartItemOrProductSku === 'object') {
      existingCartItem = existingCartItemOrProductSku;
    } else if (typeof existingCartItemOrProductSku === 'string') {
      //find first "lonely product" in the cart that doesn't have any attached services
      existingCartItem = cart?.items?.find(
        item => item.sku === existingCartItemOrProductSku && !item.properties._serviceLink
      );
    }
    if (!existingCartItem) return;

    const offeringIds = Array.isArray(offeringIdOrIds) ? offeringIdOrIds : [offeringIdOrIds];
    const relevantServiceOfferings: Array<{ variantId: number; serviceOffering: ServiceOffering }> = [];

    for (const offeringId of offeringIds) {
      let serviceOfferingOfCartItem = (
        cartServiceOfferings?.[existingCartItem.line_id] as Array<ServiceOffering>
      )?.find(service => doesContainServiceOffering(service, serviceId, offeringId));
      if (serviceOfferingOfCartItem) {
        const shopifyVariantId = await getProductServiceVariantId(serviceOfferingOfCartItem.sku);
        if (enrichServiceOfferingCallback) {
          serviceOfferingOfCartItem = enrichServiceOfferingCallback(serviceOfferingOfCartItem);
        }
        relevantServiceOfferings.push({ variantId: shopifyVariantId, serviceOffering: serviceOfferingOfCartItem });
      }
    }

    if (!relevantServiceOfferings.length) return;

    try {
      setCartLoading(true);
      await addServiceOfferings({
        offerings: relevantServiceOfferings.map(offering => ({
          variantId: offering.variantId,
          location: caller ?? location,
          serviceData: offering.serviceOffering,
        })),
        primaryLineItem: existingCartItem,
      });
    } finally {
      setCartLoading(false);
    }
  };

  const removeServiceByOfferingId = async (existingCartItem: JbCartItem, serviceId: string, offeringId: string) => {
    if (getCartLoading()) return;
    const primaryItemServiceLink = existingCartItem.properties?._serviceLink;

    //NOTE: Changed logic to avoid using first element
    //work out "attached" cart items that share the same service link as existing cart item
    //find required service cart item "attached" to existing cart item
    const attachedService = cart?.items?.find(
      item =>
        IsAttachedService(item) &&
        item.properties?._serviceLink === primaryItemServiceLink &&
        doesContainServiceOffering(tryParseJson<ServiceOffering>(item?.properties?._serviceData), serviceId, offeringId)
    );

    const lineId = attachedService?.line_id;

    if (attachedService && lineId) {
      setCartLoading(true);
      await removeAttachedServiceOffering(lineId, primaryItemServiceLink);
      setCartLoading(false);
    }
  };

  const doesContainServiceOffering = (
    serviceOffering: ServiceOffering | undefined,
    serviceId: string,
    offeringId: string
  ): boolean => {
    if (!serviceOffering) return false;
    return serviceOffering.typeId === serviceId && serviceOffering.id.includes(offeringId);
  };

  const removeAttachedService = async (item: JbCartItem) => {
    if (getCartLoading()) return;
    clearWishlistCards();

    const attachedService = item.attachedService;
    if (!attachedService) return;

    const serviceData = tryParseJson<ServiceOffering>(attachedService?.properties._serviceData);
    if (!serviceData) return;

    const gtmTrackingData = tryParseJson<ProductGtmData>(attachedService?.properties?._gtmProduct || '{}');

    setCartLoading(true);
    await removeAttachedServiceOffering(attachedService.line_id, attachedService.properties._serviceLink);
    dataLayerPushServiceEvent(
      `${serviceData.typeId}.removedfromcart`,
      gtmTrackingData,
      {
        sku: serviceData.sku,
        name: serviceData.offeringName,
        price: item.line_price / 100,
      },
      location,
      transformCartItemForMessages(item)
    );
    trackRemoveFromCartShopify(item);
  };

  const removeAttachedServices = async (primaryItem: JbCartItem, services: Array<JbCartItem>) => {
    if (getCartLoading()) return;
    clearWishlistCards();

    const servicesWithData = services
      .map(serviceItem => {
        const serviceData = tryParseJson<ServiceOffering>(serviceItem?.properties?._serviceData);
        if (!serviceData) return undefined;
        const gtmTrackingData = tryParseJson<ProductGtmData>(serviceItem?.properties?._gtmProduct || '{}');
        return { serviceItem, serviceData, gtmTrackingData };
      })
      .filter(
        (entry): entry is { serviceItem: JbCartItem; serviceData: ServiceOffering; gtmTrackingData: ProductGtmData } =>
          !!entry
      );

    if (!servicesWithData.length) return;

    const serviceLink = services[0]?.properties?._serviceLink;
    if (!serviceLink) return;

    setCartLoading(true);
    await removeAttachedServiceOffering(
      servicesWithData.map(entry => entry.serviceItem.line_id),
      serviceLink
    );
    servicesWithData.forEach(({ serviceItem }) => {
      trackRemoveFromCartShopify(serviceItem);
    });
    setCartLoading(false);
  };

  const addBundleOffering = async (item: JbCartItem) => {
    const suggestion = item.bundleSuggestions?.[0];
    if (!suggestion) return;
    setCartLoading(true);
    const properties = {
      _availabilityCanPreOrder: suggestion.availability?.canPreOrder,
      _availabilityFulfilment: suggestion.availability?.fulfilment,
      _maxQty: suggestion.product?.limitPerOrder,
      _itemListName: 'Bundle buy',
      _priceAtc: Math.round(suggestion.price * 100),
    };
    const updatedCart = await storefrontClient.addToCart(suggestion.variant_id, 1, filterAndMapAttributes(properties));
    await updateCart(updatedCart);
    trackViewCartShopify(updatedCart, location);
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        location,
        removeItem,
        clearWishlistCards,
        updateQuantity,
        removeAttachedService,
        removeAttachedServices,
        addBundleOffering,
        isMiniCartOpen,
        toggleMiniCart,
        addServiceByOfferingId,
        removeServiceByOfferingId,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCartContext = () => {
  const context = useContext(CartContext);
  if (context === undefined || context === null) {
    throw new Error('useCartContext must be used within a useCartContextProvider');
  }
  return context;
};
